import React from "react";

import { Currency } from "../../features/auth/store/types/currency-types";

type CurrencyListProps = {
  items: Currency[];
  loading?: boolean;
  onEdit?: (item: Currency) => void;
  onDelete?: (item: Currency) => void;
};

export const CurrencyTable: React.FC<CurrencyListProps> = ({
  items,
  loading,
  onEdit,
  onDelete,
}) => {
  const hasItems = items && items.length > 0;

  return (
    <div className="overflow-hidden bg-white dark:bg-zinc-900 rounded-lg border border-gray-200 dark:border-zinc-800">
      <table className="min-w-full text-left border-collapse">
        {/* Table Header: Hidden on mobile, block on tablet and up */}
        <thead className="hidden md:table-header-group bg-gray-50 dark:bg-zinc-800/50 text-xs uppercase text-gray-500 dark:text-zinc-400">
          <tr>
            <th className="px-4 py-3 font-semibold">Name</th>
            <th className="px-4 py-3 font-semibold">Code</th>
            <th className="px-4 py-3 font-semibold">Symbol</th>
            <th className="px-4 py-3 font-semibold">Exchange Rate</th>
            <th className="px-4 py-3 font-semibold text-right">Actions</th>
          </tr>
        </thead>

        {/* Table Body */}
        <tbody className="block md:table-row-group divide-y divide-gray-100 dark:divide-zinc-800">
          {loading ? (
            <tr className="block md:table-row">
              <td
                colSpan={5}
                className="block md:table-cell px-4 py-6 text-center"
              >
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                </div>
              </td>
            </tr>
          ) : hasItems ? (
            items.map((currency) => (
              <tr
                key={currency.id}
                className="block md:table-row text-sm text-gray-800 dark:text-zinc-300 p-4 md:p-0 space-y-2 md:space-y-0"
              >
                {/* Name Row/Cell */}
                <td className="flex justify-between md:table-cell md:px-4 md:py-3 border-b border-gray-50 md:border-none pb-2 md:pb-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Name
                  </span>
                  <span className="font-semibold md:font-normal text-right md:text-left">
                    {currency.name}
                  </span>
                </td>

                {/* Code Row/Cell */}
                <td className="flex justify-between md:table-cell md:px-4 md:py-3 border-b border-gray-50 md:border-none pb-2 md:pb-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Code
                  </span>
                  <span className="text-right md:text-left">
                    {currency.code || "-"}
                  </span>
                </td>

                {/* Symbol Row/Cell */}
                <td className="flex justify-between md:table-cell md:px-4 md:py-3 border-b border-gray-50 md:border-none pb-2 md:pb-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Symbol
                  </span>
                  <span className="font-semibold dark:text-white text-right md:text-left">
                    {currency.symbol}
                  </span>
                </td>

                {/* Exchange Rate Row/Cell */}
                <td className="flex justify-between md:table-cell md:px-4 md:py-3 whitespace-nowrap border-b border-gray-50 md:border-none pb-2 md:pb-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Exchange Rate
                  </span>
                  <span className="text-right md:text-left">
                    {Number(currency.exchange_rate).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 6,
                    })}
                  </span>
                </td>

                {/* Actions Row/Cell */}
                <td className="flex justify-between items-center md:table-cell md:px-4 md:py-3 pt-2 md:pt-3">
                  <span className="md:hidden font-medium text-gray-500 dark:text-zinc-400">
                    Actions
                  </span>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      className="px-3 py-1 text-xs rounded bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-500 dark:border-yellow-900/30"
                      onClick={() => onEdit?.(currency)}
                    >
                      Edit
                    </button>
                    <button
                      className="px-3 py-1 text-xs rounded bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30"
                      onClick={() => onDelete?.(currency)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr className="block md:table-row">
              <td
                colSpan={5}
                className="block md:table-cell px-4 py-6 text-center text-sm text-gray-500 dark:text-zinc-500"
              >
                No currencies available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
